import { addDays } from "date-fns";
import type { Identifier } from "ra-core";

import type { ConfigurationContextValue } from "../root/ConfigurationContext";
import { getLeadStages, getOpenStages, toStageInFilter } from "./stages";
import { ENDS_SOON_DAYS, toISODate } from "./sponsoringUtils";

type StageConfig = Pick<
  ConfigurationContextValue,
  "sponsoringStages" | "sponsoringActiveStatuses" | "sponsoringClosedStatuses"
>;

/**
 * The quick filters of the sponsorings table. They are stored as regular
 * list filter values (PostgREST `field@op` keys, understood by both the
 * fakerest and the PHP backend), so this module only translates between
 * the toggles and those keys.
 *
 * - "Momenteel actief": the active stages, not expired (no end date, or an
 *   end date from today on).
 * - "Niet verlopen": every open sponsoring (leads included) that is not
 *   expired.
 * - "Loopt binnenkort af" and "Verlopen" restrict to the active stages and
 *   an end-date window.
 * - "Leads": only the funnel stages (neither active nor closed).
 * - "Toon gesloten" lifts the default "open stages only" restriction.
 * - "Alleen van mij" filters on the owner (`sales_id`).
 *
 * The first six decide the stage/date restriction and exclude each other;
 * "not expired" is expressed as `end_date@not.lt` (NULL counts as a match).
 */
export type SponsoringQuickFilters = {
  currentlyActive: boolean;
  notExpired: boolean;
  endingSoon: boolean;
  expired: boolean;
  leads: boolean;
  showClosed: boolean;
  onlyMine: boolean;
};

export const NO_QUICK_FILTERS: SponsoringQuickFilters = {
  currentlyActive: false,
  notExpired: false,
  endingSoon: false,
  expired: false,
  leads: false,
  showClosed: false,
  onlyMine: false,
};

const EXCLUSIVE_KEYS: Array<keyof SponsoringQuickFilters> = [
  "currentlyActive",
  "notExpired",
  "endingSoon",
  "expired",
  "leads",
  "showClosed",
];

export const buildQuickFilterValues = ({
  config,
  quickFilters,
  identityId,
  today = new Date(),
}: {
  config: StageConfig;
  quickFilters: SponsoringQuickFilters;
  identityId?: Identifier;
  today?: Date;
}): Record<string, unknown> => {
  const filter: Record<string, unknown> = {};
  const activeStages = toStageInFilter(config.sponsoringActiveStatuses);
  const openStages = toStageInFilter(getOpenStages(config));
  const todayISO = toISODate(today);

  if (quickFilters.endingSoon) {
    filter["stage@in"] = activeStages;
    filter["end_date@gte"] = todayISO;
    filter["end_date@lte"] = toISODate(addDays(today, ENDS_SOON_DAYS));
  } else if (quickFilters.expired) {
    filter["stage@in"] = activeStages;
    filter["end_date@lt"] = todayISO;
  } else if (quickFilters.currentlyActive) {
    filter["stage@in"] = activeStages;
    filter["end_date@not.lt"] = todayISO;
  } else if (quickFilters.notExpired) {
    filter["stage@in"] = openStages;
    filter["end_date@not.lt"] = todayISO;
  } else if (quickFilters.leads) {
    filter["stage@in"] = toStageInFilter(getLeadStages(config));
  } else if (!quickFilters.showClosed) {
    filter["stage@in"] = openStages;
  }

  if (quickFilters.onlyMine && identityId != null) {
    filter.sales_id = identityId;
  }
  return filter;
};

/**
 * Read the toggle states back from the current list filter values.
 * `activeStages` (the `stage@in` value of the active stages) tells
 * "Momenteel actief" and "Niet verlopen" apart; without it the latter wins.
 * `leadStages` (the `stage@in` value of the funnel stages) recognises "Leads".
 */
export const readQuickFilters = (
  filterValues: Record<string, unknown> = {},
  activeStages?: string,
  leadStages?: string,
): SponsoringQuickFilters => {
  const endingSoon = filterValues["end_date@gte"] != null;
  const expired = !endingSoon && filterValues["end_date@lt"] != null;
  const hasNotExpired = filterValues["end_date@not.lt"] != null;
  const currentlyActive =
    hasNotExpired &&
    activeStages != null &&
    filterValues["stage@in"] === activeStages;
  const noDateFilter = !endingSoon && !expired && !hasNotExpired;
  return {
    currentlyActive,
    notExpired: hasNotExpired && !currentlyActive,
    endingSoon,
    expired,
    leads:
      noDateFilter &&
      leadStages != null &&
      filterValues["stage@in"] === leadStages,
    showClosed: noDateFilter && filterValues["stage@in"] == null,
    onlyMine: filterValues.sales_id != null,
  };
};

/**
 * Toggle one quick filter, keeping the mutually exclusive ones consistent.
 */
export const toggleQuickFilter = (
  current: SponsoringQuickFilters,
  key: keyof SponsoringQuickFilters,
): SponsoringQuickFilters => {
  const next = { ...current, [key]: !current[key] };
  if (next[key] && EXCLUSIVE_KEYS.includes(key)) {
    for (const other of EXCLUSIVE_KEYS) {
      if (other !== key) next[other] = false;
    }
  }
  return next;
};
